import { API } from './types'
import { h } from '@lilian1315/create-element/alien-deepsignals'
import { computed, effect, isSignal, signal, type Signal } from 'alien-deepsignals'
import { VisibilityTracker } from './visibility_tracker'
import { t } from './i18n'
import { lock, lockDecrease, lockIncrease } from './lock'
import { adsPagination, hasMoreAds, increaseIndex, setAdsPagination } from './ads_pagination'

type Message = {
  date: Date | undefined | Signal<Date | undefined>
  text: string | Node | Signal<string | Node>
  isUser: boolean
  isError?: boolean | Signal<boolean>
}

type ReactiveMessage = {
  date: Signal<Date | undefined>
  text: Signal<string | Node>
  isUser: boolean
  isError?: Signal<boolean>
}

lockIncrease()

const fullScreenBreakpoint = window.matchMedia('(max-width: 600px) or (max-height: 600px)')
const isFullscreen = signal(fullScreenBreakpoint.matches)

fullScreenBreakpoint.addEventListener('change', () => {
  isFullscreen.set(fullScreenBreakpoint.matches)
  toggleChat(false)
})

const onceOpenCallBacks = new Set<() => void>()
const isOpen = signal(sessionStorage.getItem('research_chat_open') === 'true' ? true : false)
const hasBeenOpened = signal(
  localStorage.getItem('research_chat_has_been_opened') === 'true' ? true : false
)

if (!hasBeenOpened.get()) {
  onceOpen(() => {
    hasBeenOpened.set(true)
    localStorage.setItem('research_chat_has_been_opened', 'true')
  })
}

effect(() => {
  sessionStorage.setItem('research_chat_open', isOpen.get() ? 'true' : 'false')
})

document.querySelectorAll('[data-open-research-chat]').forEach((el) => {
  el.addEventListener('click', () => toggleChat(true))
})

const bc = new BroadcastChannel('research_chat_channel')

const messageElementMap = new WeakMap<Message, HTMLElement>()
const messages = signal<Message[]>([])
const messagesEls = computed(() => messages.get().map(createMessageEl).reverse())

const loadMoreSpan = h('span')
loadMoreSpan.innerHTML = t('loadMoreProperties')

const messagesContainer = h(
  'div',
  { class: 'research-chat-messages' },
  h(
    'button',
    {
      class: 'research-chat-load-next',
      disabled: computed(() => !(hasMoreAds.get() && !lock.get())),
      onclick: loadNextAdsPage,
    },
    loadMoreSpan
  ),
  messagesEls
)

window.addEventListener('beforeunload', () => {
  sessionStorage.setItem('research_chat_scroll_top', messagesContainer.scrollTop.toString())
})

const inputFocused = signal(false)

const messageInput = h('textarea', {
  class: 'research-chat-input',
  placeholder: t('placeholder'),
  autocomplete: 'off',
  rows: 1,
  name: 'message',
  required: true,
  oninput: (e) => {
    const target = e.target as HTMLTextAreaElement
    target.style.height = 'auto'
    target.style.height = `${target.scrollHeight}px`
  },
  onkeydown: (e) => {
    if (e.key === 'Enter' && !e.shiftKey) onsubmit(e)
  },
  onfocus: () => inputFocused.set(true),
  onblur: () => inputFocused.set(false),
})

const researchChatAvatar = h('img', {
  class: 'research-chat-avatar',
  src: '/theme/swixim/images/alice_round.png',
})

const researchChatBubble = h('span', {
  class: {
    'research-chat-bubble': true,
    'show': computed(() => hasBeenOpened.get() && !isOpen.get()),
  },
  onclick: () => toggleChat(true),
})

const root = h(
  'div',
  {
    class: 'research-chat',
  },
  h(
    'div',
    { class: 'research-chat-header', onclick: () => toggleChat(false) },
    h('span', { class: 'research-chat-header-content' }, researchChatAvatar, t('assistantName'))
  ),
  messagesContainer,
  h(
    'div',
    { class: 'research-chat-footer' },
    h(
      'form',
      {
        class: {
          'research-chat-form': true,
          'focused': inputFocused,
        },
        onsubmit,
      },
      h('img', {
        class: 'research-chat-form-icon',
        src: '/theme/swixim/images/ico_loupe.svg',
        onclick: () => toggleInputFocus(true),
      }),
      messageInput,
      h('button', {
        class: 'research-chat-send-button',
        type: 'submit',
        disabled: lock,
      })
    )
  )
)

const wrapper = h(
  'div',
  {
    class: {
      'research-chat-wrapper': true,
      'locked': lock,
      'open': isOpen,
      'fullscreen': isFullscreen,
    },
  },
  root
)

const visibilityTracker = new VisibilityTracker(root)

document.body.appendChild(wrapper)
document.body.appendChild(researchChatBubble)

const getHistoryPromise = getHistory()
  .then(() => {
    bc.addEventListener('message', (event) => {
      pushMessagesFromResponse(event.data, undefined, false, true)
    })

    if (messages.get().length === 0) sendMessage(t('firstClientMessage'), false, false)

    const sessionScrollTop = parseFloat(sessionStorage.getItem('research_chat_scroll_top') ?? '')
    if (!isNaN(sessionScrollTop)) messagesContainer.scrollTop = sessionScrollTop
  })
  .finally(() => lockDecrease())

onceOpen(() => {
  getHistoryPromise.then(() => {
    if (messages.get().length === 0) {
      lockIncrease()
      pushResponseToHistory(t('firstResponseMessage'))
      pushResponseToHistory(t('secondResponseMessage'))
      const firstText = signal<Node | string>(h('div', { class: 'research-chat-thinking-loader' }))
      const secondText = signal<Node | string>(h('div', { class: 'research-chat-thinking-loader' }))

      const firstWelcomeMessage: Message = {
        date: new Date(),
        text: firstText,
        isUser: false,
      }

      const secondWelcomeMessage: Message = {
        date: new Date(),
        text: secondText,
        isUser: false,
      }

      pushMessages([firstWelcomeMessage], true)

      setTimeout(() => {
        firstText.set(t('firstResponseMessage'))

        setTimeout(() => {
          pushMessages([secondWelcomeMessage], true)

          setTimeout(() => {
            secondText.set(t('secondResponseMessage'))
            lockDecrease()
          }, 700)
        }, 200)
      }, 600)
    }
  })
})

function onsubmit(e?: Event) {
  if (e) e.preventDefault()
  if (lock.get()) return

  const text = messageInput.value.trim()
  if (text === '') return

  sendMessage(text)
  messageInput.value = ''
  messageInput.style.height = ''
}

function toggleInputFocus(force: boolean) {
  if (force) messageInput.focus()
  else messageInput.blur()

  inputFocused.set(force)
}

function openChat() {
  if (isOpen.get()) return
  isOpen.set(true)
  toggleInputFocus(true)

  for (const callback of onceOpenCallBacks) callback()
  onceOpenCallBacks.clear()
}

function closeChat() {
  if (!isOpen.get()) return
  isOpen.set(false)
  messageInput.blur()
}

let chatAnimation: Animation | null = null
let bubbleAnimation: Animation | null = null

function toggleChat(force: boolean) {
  if (force === isOpen.get()) return

  const width = wrapper.offsetWidth
  const height = wrapper.offsetHeight
  let rootWidth = root.offsetWidth
  let rootHeight = root.offsetHeight

  if (chatAnimation) chatAnimation.cancel()

  if (force) {
    openChat()
    // @ts-ignore
    if (fullScreenBreakpoint.matches) freezeScroll()
  } else {
    closeChat()
    // @ts-ignore
    if (fullScreenBreakpoint.matches) freezeScroll(true)
  }

  const targetWidth = wrapper.scrollWidth
  const targetHeight = wrapper.scrollHeight
  if (rootWidth === 0) rootWidth = wrapper.offsetWidth
  if (rootHeight === 0) rootHeight = wrapper.offsetHeight

  wrapper.style.display = 'block'
  root.style.width = `${rootWidth}px`
  root.style.height = `${rootHeight}px`

  researchChatBubble.style.display = 'block'
  researchChatAvatar.style.opacity = '0'
  const avatarRect = researchChatAvatar.getBoundingClientRect()
  const bubbleRect = researchChatBubble.getBoundingClientRect()
  const bubbleDeltaX = avatarRect.left - bubbleRect.left
  const bubbleDeltaY = avatarRect.top - bubbleRect.top
  const bubbleDeltaWidth = avatarRect.width / bubbleRect.width
  const bubbleDeltaHeight = avatarRect.height / bubbleRect.height

  const bubbleTransform = `
    translate(${bubbleDeltaX}px, ${bubbleDeltaY}px)
    scale(${bubbleDeltaWidth * 100}%, ${bubbleDeltaHeight * 100}%)
  `

  chatAnimation = wrapper.animate(
    [
      {
        right: force ? '-20px' : '',
        bottom: force ? '-20px' : '',
        width: `${width}px`,
        height: `${height}px`,
      },
      {
        right: force ? '' : '-20px',
        bottom: force ? '' : '-20px',
        width: `${targetWidth}px`,
        height: `${targetHeight}px`,
      },
    ],
    {
      duration: 300,
      easing: 'cubic-bezier(0.66, 0, 0.34, 1)',
    }
  )

  bubbleAnimation = researchChatBubble.animate(
    [
      {
        transform: force ? '' : bubbleTransform,
        boxShadow: force ? '0 2px 10px rgba(0,0,0,0.5)' : '0 2px 10px rgba(0,0,0,0)',
      },
      {
        transform: force ? bubbleTransform : '',
        boxShadow: force ? '0 2px 10px rgba(0,0,0,0)' : '0 2px 10px rgba(0,0,0,0.5)',
      },
    ],
    {
      duration: 300,
      easing: 'cubic-bezier(0.66, 0, 0.34, 1)',
    }
  )

  const cleanup = () => {
    wrapper.style.display = ''
    root.style.width = ''
    root.style.height = ''

    if (!chatAnimation) return

    chatAnimation.removeEventListener('finish', cleanup)
    chatAnimation.removeEventListener('cancel', cleanup)
    chatAnimation.removeEventListener('remove', cleanup)
    chatAnimation = null
  }

  chatAnimation.addEventListener('finish', cleanup)
  chatAnimation.addEventListener('cancel', cleanup)
  chatAnimation.addEventListener('remove', cleanup)

  const bubbleCleanup = () => {
    researchChatBubble.style.display = ''
    researchChatAvatar.style.opacity = ''

    if (!bubbleAnimation) return

    bubbleAnimation.removeEventListener('finish', bubbleCleanup)
    bubbleAnimation.removeEventListener('cancel', bubbleCleanup)
    bubbleAnimation.removeEventListener('remove', bubbleCleanup)
    bubbleAnimation = null
  }

  bubbleAnimation.addEventListener('finish', bubbleCleanup)
  bubbleAnimation.addEventListener('cancel', bubbleCleanup)
  bubbleAnimation.addEventListener('remove', bubbleCleanup)
}

function onceOpen(callback: () => void) {
  if (isOpen.get()) callback()
  else onceOpenCallBacks.add(callback)
}

function sendMessage(text: string, displayMessage?: boolean, displayResponse?: boolean) {
  lockIncrease()
  if (displayMessage !== false) {
    pushMessagesFromResponse(
      {
        type: API.ChatType.Message,
        timestamp: Math.floor(Date.now() / 1000),
        data: {
          message: text,
        },
      },
      undefined,
      true
    )
  }

  const data = new FormData()
  data.append('message', text)
  data.append('push_message_to_history', displayMessage === false ? '0' : '1')
  data.append('push_response_to_history', displayResponse === false ? '0' : '1')

  const responseMessage = {
    date: signal<Date | undefined>(undefined),
    text: signal<Node | string>(
      h(
        'div',
        { class: 'research-chat-thinking' },
        t('thinking'),
        h('div', { class: 'research-chat-thinking-loader' })
      )
    ),
    isUser: false,
    isError: signal(false),
  }

  if (displayResponse !== false) pushMessages([responseMessage], true)

  fetchResearchChatAPI('message', {
    method: 'POST',
    body: data,
  })
    .then((response) => response.json())
    .then((res: API.BotChatEntry) => {
      if (res.type === API.ChatType.Response) {
        if (res.data.ads && res.data.ads.length > 0 && res.data.total_items_count) {
          setAdsPagination({ size: res.data.ads.length, total: res.data.total_items_count })
        } else {
          setAdsPagination(undefined)
        }
      }
      if (displayResponse !== false || res.type === API.ChatType.Error) {
        pushMessagesFromResponse(res, res.type === API.ChatType.Error ? undefined : responseMessage)
      }
    })
    .catch(() => {
      responseMessage.isError.set(true)
      responseMessage.text.set(t('genericError'))
    })
    .finally(lockDecrease)
}

function getAdsPage(index: number, size: number) {
  lockIncrease()
  return fetchResearchChatAPI(
    'ads-page',
    { method: 'GET' },
    { index: index.toString(), size: size.toString() }
  )
    .then((response) => response.json())
    .then((res: API.ChatAdsPage) => {
      pushMessagesFromResponse(res)
      increaseIndex()
    })
    .catch(() => {
      pushMessages(
        [
          {
            date: new Date(),
            text: t('genericError'),
            isUser: false,
            isError: true,
          },
        ],
        true
      )
    })
    .finally(lockDecrease)
}

function loadNextAdsPage() {
  const pagination = adsPagination.get()
  if (!pagination || !hasMoreAds.get()) return

  getAdsPage(pagination.index.get() + 1, pagination.size)
}

function createMessageEl(message: Message) {
  let messageEl = messageElementMap.get(message)

  if (messageEl) return messageEl

  const dateEl = computed<Node | null>(() => {
    const date = isSignal(message.date) ? message.date.get() : message.date
    if (!date) return null

    return h(
      'div',
      { class: 'research-chat-message-date' },
      date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    )
  })

  messageEl = h(
    'div',
    {
      class: {
        'research-chat-message': true,
        'research-chat-message--user': message.isUser,
        'research-chat-message--bot': !message.isUser,
        'research-chat-message--error': isSignal(message.isError)
          ? message.isError
          : !!message.isError,
      },
    },
    createMessageTriangle(message.isUser),
    dateEl,
    h('div', { class: 'research-chat-message-text' }, message.text)
  )

  messageElementMap.set(message, messageEl)

  return messageEl
}

function createMessageTriangle(isUser: boolean): SVGElement {
  const svg = h(
    'svg',
    {
      class: 'research-chat-message-triangle',
      width: '28',
      height: '23',
      viewBox: isUser ? '-0.5 -0.5 27.5 22.5' : '0 0 28 23',
    },
    h('svg:path', {
      d: 'M4.08928 0.5H24.3567C25.9348 0.5 26.8913 2.2423 26.044 3.57375L15.9524 19.4319C15.3836 20.3257 14 19.9228 14 18.8633C14 18.6279 13.9215 18.3991 13.7769 18.2132L2.51057 3.72788C1.4888 2.41417 2.42498 0.5 4.08928 0.5Z',
      fill: isUser ? 'currentColor' : 'white',
    }),
    !isUser
      ? h('svg:path', {
          d: 'M28 0.5H4.08928C2.42498 0.5 1.4888 2.41417 2.51057 3.72788L13.5787 17.9583C13.8518 18.3094 14 18.7415 14 19.1862V22.5',
          stroke: 'currentColor',
          strokeWidth: 1,
          fill: 'none',
        })
      : null
  )

  return svg
}

function getHistory() {
  return fetchResearchChatAPI('history', {
    method: 'GET',
  })
    .then((response) => response.json())
    .then((res: API.ChatHistoryResponse) => {
      res.data.forEach((entry) => pushMessagesFromResponse(entry, undefined, false, true))
    })
}

function pushResponseToHistory(message: string) {
  const data = new FormData()
  data.append('message', message)
  return fetchResearchChatAPI('push-response-to-history', {
    method: 'POST',
    body: data,
  })
}

function pushMessagesFromResponse(
  response: API.ChatEntry,
  message?: ReactiveMessage,
  autoscroll?: boolean,
  noBroadcast?: boolean
) {
  if (!noBroadcast) bc.postMessage(response)
  const date = new Date(response.timestamp * 1000)

  if (!message && response.type !== API.ChatType.AdsPage) {
    message = {
      date: signal(date),
      text: signal(t('loading')),
      isUser: response.type === API.ChatType.Message,
    }

    pushMessages([message], autoscroll)
  } else if (message) {
    message.date.set(date)
  }

  switch (response.type) {
    case API.ChatType.Message:
      message?.text.set(response.data.message)
      break
    case API.ChatType.Response:
      message?.text.set(response.data.message)
      if (response.data.ads) pushMessagesFromAds(response.data.ads, autoscroll)
      break
    case API.ChatType.AdsPage:
      if (response.data.ads) pushMessagesFromAds(response.data.ads, autoscroll)
      break
    case API.ChatType.Legal:
      message?.text.set(
        h(
          'p',
          null,
          h(
            'a',
            {
              href: response.data.policy,
              target: '_blank',
              download: t('legalPolicy'),
            },
            t('legalPolicy')
          ),
          h('br'),
          h(
            'a',
            {
              href: response.data.term_of_service,
              target: '_blank',
              download: t('legalTermOfService'),
            },
            t('legalTermOfService')
          )
        )
      )
      break
    default:
      message?.text.set(t('genericError'))
      break
  }

  scrollToBottom()
}

function pushMessages(newMessages: Message[], autoscroll?: boolean) {
  messages.set([...messages.get(), ...newMessages])
  if (autoscroll === true) scrollToBottom()
}

function fetchAd(ref: string): Promise<string> {
  ref = ref.toLowerCase()
  return fetch(`/annonces/ref-${ref}?ajax=true&ajax_chat_annonce=true`).then((response) => {
    if (!response.ok || !response.url.includes(`/ref-${ref}/`)) {
      console.error('Failed to fetch property:', ref)
      throw new FetchAdError(t('adLoadError'))
    }
    return response.text()
  })
}

function pushMessagesFromAds(ads: string[], autoscroll?: boolean) {
  for (let ad of ads) {
    if (typeof ad !== 'string') continue

    const adMessage = {
      date: undefined,
      text: signal(h('div', { class: 'research-chat-property' }, t('loadingProperty'))),
      isUser: false,
      isError: signal(false),
    }

    pushMessages([adMessage], autoscroll)

    visibilityTracker.observe(adMessage.text.get(), (visible) => {
      if (!visible) return

      visibilityTracker.unobserve(adMessage.text.get())

      fetchAd(ad)
        .then((adContent) => {
          adMessage.text.get().innerHTML = adContent
          // @ts-ignore
          setUpButtons(adMessage.text.get())
        })
        .catch((error) => {
          if (error instanceof FetchAdError) {
            adMessage.isError.set(true)
            adMessage.text.get().innerHTML = error.message
          } else {
            adMessage.isError.set(true)
            adMessage.text.get().innerHTML = t('genericError')
          }
        })
    })
  }
}

function scrollToBottom() {
  messagesContainer.scrollTo({
    left: 0,
    top: messagesContainer.scrollHeight,
    behavior: 'smooth',
  })
}

function fetchResearchChatAPI(
  route: string,
  options?: RequestInit,
  queryParameters?: Record<string, string>
) {
  const parameters = new URLSearchParams(queryParameters)
  parameters.set('route', '/' + route)
  parameters.set('lang', document.documentElement.lang || 'fr')

  return fetch(`/theme/swixim/research_chat/api.php?${parameters}`, options)
}

class FetchAdError extends Error {}
